import { services } from '@repo/ui/lib/api/services/index';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ProfilCreateInput, ProfilFilters, ProfilListResponse, ProfilResponse, ProfilSelectOption, ProfilUpdateInput } from '../../../lib/api/types/profil';
import { useQueryWithConfig } from '../useQueryWithConfig';
// Hooks for fetching profiles
export const useProfils = (filters: ProfilFilters = {}) => {
  const { page = 1, limit = 10, sortBy, sortOrder, ...filterParams } = filters;
  const keys = ["profils", "list", page, limit, sortBy, sortOrder, JSON.stringify(filterParams)].filter(Boolean);
  
  return useQueryWithConfig<ProfilListResponse>(
    keys as string[],
    () => services.profil.getProfils(filters)
  );
};

export const useProfil = (id: number) => {
  return useQueryWithConfig<ProfilResponse>(
    ["profils", "detail", id],
    () => services.profil.getProfilById(id),
    {
      enabled: !!id,
    }
  );
};

export const useProfilOptions = () => {
  return useQueryWithConfig<ProfilSelectOption[]>(
    ["profils", "options"],
    () => services.profil.getProfilOptions()
  );
};

// Hooks for profile mutations
export const useCreateProfil = () => {
  const queryClient = useQueryClient();
  
  return useMutation<ProfilResponse, Error, ProfilCreateInput>({
    mutationFn: (data) => services.profil.createProfil(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profils", "list"] });
      queryClient.invalidateQueries({ queryKey: ["profils", "options"] });
    },
  });
};

export const useUpdateProfil = () => {
  const queryClient = useQueryClient();
  
  return useMutation<ProfilResponse, Error, { id: number; data: ProfilUpdateInput }>({
    mutationFn: ({ id, data }) => services.profil.updateProfil(id, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["profils", "detail", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["profils", "list"] });
      queryClient.invalidateQueries({ queryKey: ["profils", "options"] });
    },
  });
};

export const useDeleteProfil = () => {
  const queryClient = useQueryClient();
  
  return useMutation<ProfilResponse, Error, number>({
    mutationFn: (id) => services.profil.deleteProfil(id),
    onSuccess: (_, id) => {
      queryClient.invalidateQueries({ queryKey: ["profils", "detail", id] });
      queryClient.invalidateQueries({ queryKey: ["profils", "list"] });
      queryClient.invalidateQueries({ queryKey: ["profils", "options"] });
    },
  });
};
